import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import styled from 'styled-components';

import CheckIcon from '../../../assets/svg/check-icon.svg';
import { Price, Product } from '../../../types/subscription';
import { BasicSpinner } from '../../../ui';
import { StyledHr } from '../../../ui/Styled';

type PricingTableProps = {
  product: Product;
  onSelect: (price: Price) => void;
  requestPending: boolean;
  disabled?: boolean;
};

type ToggleOption = 'month' | 'year';

/* ---------- Styled Components ---------- */

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1rem auto 2rem;
  border-radius: ${(props) => props.theme.buttons.base.borderRadius};
  overflow: hidden;

  background: ${({ theme }) => theme.colors.gradients.tertiary}, rgba(255, 255, 255, 0.6);
  background-blend-mode: overlay;
  position: relative;
`;

const ToggleButton = styled.button<{ $active?: boolean }>`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  padding: 0.6rem 0.2rem;
  border-radius: ${(props) => props.theme.buttons.base.borderRadius};
  background: ${({ $active, theme }) => ($active ? theme.colors.gradients.tertiary : 'none')};
  background-blend-mode: overlay;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${(props) => props.theme.fonts.fontWeights.semibold};
  cursor: pointer;
  transition:
    background 0.3s ease-in-out,
    color 0.3s ease-in-out;
  white-space: nowrap;
`;

const Stage = styled.div<{ $enterX: string; $exitX: string; $height: number }>`
  --enter-x: ${({ $enterX }) => $enterX};
  --exit-x: ${({ $exitX }) => $exitX};
  position: relative;
  overflow: hidden;
  margin: 0 5px;
  height: ${({ $height }) => `${$height + 10}px`};
  transition: height 300ms ease-in-out;
`;

const SlidePane = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  &.slide-enter {
    opacity: 0;
    transform: translateX(var(--enter-x));
  }
  &.slide-enter-active {
    opacity: 1;
    transform: translateX(0%);
    transition:
      transform 300ms ease-in-out,
      opacity 300ms ease-in-out;
  }
  &.slide-exit {
    opacity: 1;
    transform: translateX(0%);
  }
  &.slide-exit-active {
    opacity: 0;
    transform: translateX(var(--exit-x));
    transition:
      transform 300ms ease-in-out,
      opacity 300ms ease-in-out;
  }
`;

const Card = styled.div`
  border: 1px solid ${(props) => props.theme.colors.light};
  margin: 0;
  padding: 0.8rem;
  text-align: center;
  cursor: pointer;
  border-radius: 2px;
  background: #fff;
`;

const StyledPrice = styled.span`
  color: ${(props) => props.theme.colors.secondary};
  font-size: 32px;
  font-weight: ${(props) => props.theme.fonts.fontWeights.semibold};
`;
const IntervalLabel = styled.span`
  color: ${(props) => props.theme.colors.secondary};
  font-weight: ${(props) => props.theme.fonts.fontWeights.semibold};
`;

/* ---------- Component ---------- */

const PricingTable = ({ product, onSelect, requestPending, disabled = false }: PricingTableProps) => {
  const [selectedInterval, setSelectedInterval] = useState<ToggleOption>('month');
  const [enterX, setEnterX] = useState<string>('30%');
  const [exitX, setExitX] = useState<string>('-30%');
  const [height, setHeight] = useState<number>(0);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const filteredPrice = useMemo(
    () => product.prices.find((p) => p.recurring.interval === selectedInterval),
    [product.prices, selectedInterval]
  );

  const handleIntervalChange = (val: ToggleOption) => {
    if (val === selectedInterval) return;

    // Monthly → Yearly: monthly exits left, yearly enters from right
    if (selectedInterval === 'month' && val === 'year') {
      setEnterX('30%');
      setExitX('-30%');
    }
    // Yearly → Monthly: yearly exits right, monthly enters from left
    if (selectedInterval === 'year' && val === 'month') {
      setEnterX('-30%');
      setExitX('30%');
    }
    setSelectedInterval(val);
  };

  // Measure card height dynamically
  useLayoutEffect(() => {
    if (nodeRef.current) {
      const card = nodeRef.current.querySelector('[data-card]');
      if (card instanceof HTMLElement) setHeight(card.offsetHeight);
    }
  }, [selectedInterval, filteredPrice]);

  return (
    <div>
      <ToggleWrapper className="mx-1">
        <ToggleButton $active={selectedInterval === 'month'} onClick={() => handleIntervalChange('month')}>
          Billed monthly
        </ToggleButton>
        <ToggleButton $active={selectedInterval === 'year'} onClick={() => handleIntervalChange('year')}>
          Billed yearly
        </ToggleButton>
      </ToggleWrapper>

      <Stage $enterX={enterX} $exitX={exitX} $height={height}>
        <SwitchTransition>
          <CSSTransition
            key={selectedInterval}
            timeout={300}
            classNames="slide"
            nodeRef={nodeRef}
            unmountOnExit
            onEnter={() => {
              if (nodeRef.current) {
                const card = nodeRef.current.querySelector('[data-card]');
                if (card instanceof HTMLElement) setHeight(card.offsetHeight);
              }
            }}
          >
            <SlidePane ref={nodeRef}>
              {filteredPrice && (
                <div data-card>
                  <Card>
                    <div className="price-display mb-3 mt-2 text-left">
                      <StyledPrice>£{filteredPrice.unit_amount / 100}</StyledPrice>
                      <IntervalLabel>/{selectedInterval}</IntervalLabel>
                    </div>
                    <StyledHr />
                    <div className="text-start">
                      <p className="mb-1 text-left">Subscription includes:</p>
                      <ul className="list-unstyled mb-0">
                        {product.marketing_features.map((f, i) => (
                          <li key={i} className="mb-1 d-flex align-items-start gap-2 text-left">
                            <span className="text-primary mr-1">
                              <img src={CheckIcon} alt="" />
                            </span>
                            <span>{f.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                  <Button disabled={requestPending || disabled} onClick={() => onSelect(filteredPrice)} block>
                    {requestPending ? <BasicSpinner color="white" /> : 'Start 30 day trial'}
                  </Button>
                </div>
              )}
            </SlidePane>
          </CSSTransition>
        </SwitchTransition>
      </Stage>
    </div>
  );
};

export default PricingTable;
